import {
  initVersionedSchema,
  toMentionContent,
  toTextContent,
} from '@principle-theorem/editor';
import {
  IDestinationEntityJobRunOptions,
  IGetRecordResponse,
  IHasSourceIdentifier,
  ITranslationMap,
  type FailedDestinationEntityRecord,
  type IDestinationEntity,
  type IDestinationEntityRecord,
  type IPracticeMigration,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  Interaction,
  stafferToNamedDoc,
  toMention,
} from '@principle-theorem/principle-core';
import {
  InteractionType,
  MentionResourceType,
  type IInteractionV2,
  type IPatient,
  type IStaffer,
} from '@principle-theorem/principle-core/interfaces';
import {
  ISODateType,
  Timezone,
  WithPrimaryKey,
  resolveSequentially,
  snapshotCombineLatest,
  toMomentTz,
  toTimestamp,
  type WithRef,
} from '@principle-theorem/shared';
import { compact } from 'lodash';
import { Observable, combineLatest, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';
import { PATIENT_RESOURCE_TYPE } from '../../../destination/entities/patient';
import {
  BasePatientInteractionDestinationEntity,
  IBasePatientInteractionJobData,
  IPatientInteractionMigrationData,
} from '../../../destination/entities/patient-interactions';
import { STAFFER_RESOURCE_TYPE } from '../../../destination/entities/staff';
import {
  ITEM_CODE_NOTE_TO_INTERACTION_MAP,
  ItemCodeNoteType,
} from '../../../mappings/item-codes-to-notes-xlsx';
import { TranslationMapHandler } from '../../../translation-map';
import {
  ExactPatient,
  IExactPatient,
  PatientSourceEntity,
} from '../../source/entities/patient';
import { PatientNotesSourceEntity } from '../../source/entities/patient-notes';
import {
  ExactTreatment,
  PatientTreatmentSourceEntity,
} from '../../source/entities/patient-treatments';
import { ExactItemCodeToNoteMappingHandler } from '../mappings/item-code-to-notes';
import {
  ExactStafferMappingHandler,
  resolveExactStaffer,
} from '../mappings/staff';
import { PatientDestinationEntity } from './patient';

export interface IPatientInteractionJobData
  extends IBasePatientInteractionJobData<ExactPatient> {
  staff: WithPrimaryKey<ITranslationMap<IStaffer>>[];
  itemCodesToNotes: WithPrimaryKey<ITranslationMap<object, ItemCodeNoteType>>[];
}

export function buildCommunityServiceCardInteraction(
  patient: IExactPatient,
  backupDate: ISODateType,
  timezone: Timezone
): (IInteractionV2 & IHasSourceIdentifier) | undefined {
  const cardNumber = patient.community_service_cardnum;
  if (!cardNumber) {
    return;
  }

  const expiry = patient.community_service_cardexpiry;
  const content = expiry
    ? `Community Service Card: ${cardNumber} Expiry: ${expiry}`
    : `Community Service Card: ${cardNumber}`;

  return {
    sourceIdentifier: `${patient.patient_id}-communityServiceNote`,
    ...Interaction.init({
      type: InteractionType.Note,
      owner: undefined,
      title: [toTextContent('Unknown user'), toTextContent(' added a Note')],
      pinned: true,
      createdAt: toTimestamp(toMomentTz(backupDate, timezone)),
      content: initVersionedSchema(content),
    }),
  };
}

export class PatientInteractionsDestinationEntity extends BasePatientInteractionDestinationEntity<
  ExactPatient,
  IPatientInteractionJobData
> {
  patientSourceEntity = new PatientSourceEntity();

  override sourceEntities = {
    patients: new PatientSourceEntity(),
    notes: new PatientNotesSourceEntity(),
    treatments: new PatientTreatmentSourceEntity(),
  };

  override destinationEntities = {
    patients: new PatientDestinationEntity(),
  };

  customMappings = {
    staff: new ExactStafferMappingHandler(),
    itemCodesToNotes: new ExactItemCodeToNoteMappingHandler(),
  };

  buildJobData$(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    runOptions: IDestinationEntityJobRunOptions
  ): Observable<IPatientInteractionJobData[]> {
    const staff$ = combineLatest([
      this.customMappings.staff.getRecords(translationMap),
      translationMap.getByType<IStaffer>(STAFFER_RESOURCE_TYPE),
    ]).pipe(map(([staff, mappedStaff]) => [...staff, ...mappedStaff]));
    const itemCodesToNotes$ =
      this.customMappings.itemCodesToNotes.getRecords(translationMap);

    return snapshotCombineLatest([staff$, itemCodesToNotes$]).pipe(
      switchMap((dependencies) =>
        combineLatest([
          this.buildSourceRecordQuery$(
            migration,
            this.sourceEntities.patients,
            runOptions
          ),
          of(dependencies),
        ])
      ),
      map(([patients, [staff, itemCodesToNotes]]) =>
        patients.map((sourcePatient) => ({
          sourcePatient,
          staff,
          itemCodesToNotes,
        }))
      )
    );
  }

  async buildMigrationData(
    migration: WithRef<IPracticeMigration>,
    _destinationEntity: WithRef<IDestinationEntity>,
    translationMap: TranslationMapHandler,
    data: IPatientInteractionJobData
  ): Promise<
    | IPatientInteractionMigrationData
    | (IDestinationEntityRecord & FailedDestinationEntityRecord)
  > {
    const sourcePatientId = data.sourcePatient.data.patient_id;
    const patientRef = await translationMap.getDestination<IPatient>(
      sourcePatientId,
      PATIENT_RESOURCE_TYPE
    );

    if (!patientRef) {
      return this._buildErrorResponse(
        data.sourcePatient.record,
        `Couldn't resolve patient`
      );
    }

    const communityCardInteraction = buildCommunityServiceCardInteraction(
      data.sourcePatient.data,
      migration.configuration.backupDate,
      migration.configuration.timezone
    );

    const patientNotes = await this.sourceEntities.notes.filterRecords(
      migration,
      'patientId',
      sourcePatientId
    );

    const interactions = await resolveSequentially(
      patientNotes,
      async (note) => {
        const staffer = await resolveExactStaffer(
          note.data.user_id ?? '',
          translationMap,
          data.staff
        );

        const sourceIdentifier = this.sourceEntities.notes.getSourceRecordId(
          note.data
        );
        const pinned = note.data.is_pop_up && !note.data.is_dismissed;

        return {
          sourceIdentifier,
          ...Interaction.init({
            type: InteractionType.Note,
            owner: staffer ? stafferToNamedDoc(staffer) : undefined,
            title: [
              staffer
                ? toMentionContent(
                    toMention(staffer, MentionResourceType.Staffer)
                  )
                : toTextContent('Unknown user'),
              toTextContent(` added a Note`),
            ],
            pinned,
            createdAt: note.record.translations.entryDate,
            content: initVersionedSchema(note.data.note || []),
          }),
        };
      }
    );

    const patientNoteTreatments =
      await this.sourceEntities.treatments.filterRecords(
        migration,
        'patientId',
        sourcePatientId,
        (record) =>
          !!data.itemCodesToNotes.find(
            (mappedNoteCode) =>
              mappedNoteCode.sourceIdentifier === record.data.service_code
          ) && !!record.data.treatment_notes
      );

    const interactionsFromTreatments =
      await this._buildInteractionsFromTreatment(
        patientNoteTreatments,
        data,
        migration,
        translationMap
      );

    return {
      patientRef,
      interactions: compact([
        ...interactions,
        ...interactionsFromTreatments,
        communityCardInteraction,
      ]),
    };
  }

  private async _buildInteractionsFromTreatment(
    patientNoteTreatments: IGetRecordResponse<ExactTreatment>[],
    data: IPatientInteractionJobData,
    migration: WithRef<IPracticeMigration>,
    translationMap: TranslationMapHandler
  ): Promise<(IInteractionV2 & IHasSourceIdentifier)[]> {
    const interactions = await resolveSequentially(
      patientNoteTreatments,
      async (treatment) => {
        const mappedNote = data.itemCodesToNotes.find(
          (mappedNoteCode) =>
            mappedNoteCode.sourceIdentifier === treatment.data.service_code
        );
        if (
          !mappedNote?.destinationValue ||
          [
            ItemCodeNoteType.Omit,
            ItemCodeNoteType.ClinicalNote,
            ItemCodeNoteType.PatientSocialNote,
            ItemCodeNoteType.SterilisationRecord,
          ].includes(mappedNote.destinationValue.value)
        ) {
          return;
        }

        const staffer = await resolveExactStaffer(
          treatment.data.provider_code ?? '',
          translationMap,
          data.staff
        );
        const sourceIdentifier =
          this.sourceEntities.treatments.getSourceRecordId(treatment.data);
        const type =
          ITEM_CODE_NOTE_TO_INTERACTION_MAP[mappedNote.destinationValue.value];
        const createdAt = treatment.record.translations.plannedDate
          ? toTimestamp(
              toMomentTz(
                treatment.record.translations.plannedDate,
                migration.configuration.timezone
              )
            )
          : undefined;
        const treatmentContent = treatment.data.treatment_notes ?? '';

        return {
          sourceIdentifier,
          ...Interaction.init({
            type,
            owner: staffer ? stafferToNamedDoc(staffer) : undefined,
            title: [
              staffer
                ? toMentionContent(
                    toMention(staffer, MentionResourceType.Staffer)
                  )
                : toTextContent('Unknown user'),
              toTextContent(` added a ${type} note`),
            ],
            createdAt,
            content: initVersionedSchema(treatmentContent || []),
          }),
        };
      }
    );

    return compact(interactions);
  }
}
